import type { ReplicaRecipe } from "./replica";

/**
 * Which recipe a sale line consumes (FR-067, FR-117, data-model.md
 * "Resolution order"). Pure: replica in, quantities out.
 *
 *   1. The recipe for (item, variant) if one exists, else (item, NULL).
 *   2. PLUS each selected modifier's recipe — (item, variant, modifier) if one
 *      exists, else (item, NULL, modifier). Add-ons add; they never replace.
 *
 * A line is MAPPED when step 1 finds a recipe, even an empty one: "bottled
 * water consumes nothing we track" is a decision, not an oversight. An add-on
 * recipe alone does not map a line — the drink itself still consumed something
 * nobody has described — but its quantities are still deducted, because that
 * add-on was really used.
 */

export interface SaleLineInput {
  lineId: string;
  menuItemId: string;
  variantId: string | null;
  modifierIds: readonly string[];
  quantity: number;
  itemName: string;
}

export interface PerUnit {
  ingredientId: string;
  /** Thousandths of the ingredient's unit, per ONE unit sold. */
  quantity: number;
}

export interface ResolvedLine {
  lineId: string;
  menuItemId: string;
  itemName: string;
  quantity: number;
  mapped: boolean;
  /** Summed per ingredient; an ingredient that nets to zero is left out. */
  perUnit: PerUnit[];
}

export type RecipeIndex = ReadonlyMap<string, ReplicaRecipe>;

const key = (menuItemId: string, variantId: string | null, modifierId: string | null) =>
  `${menuItemId}|${variantId ?? ""}|${modifierId ?? ""}`;

export function indexRecipes(recipes: readonly ReplicaRecipe[]): RecipeIndex {
  const index = new Map<string, ReplicaRecipe>();
  for (const recipe of recipes) {
    index.set(key(recipe.menuItemId, recipe.variantId, recipe.modifierId), recipe);
  }
  return index;
}

function find(
  index: RecipeIndex,
  menuItemId: string,
  variantId: string | null,
  modifierId: string | null,
): ReplicaRecipe | undefined {
  return (
    (variantId !== null ? index.get(key(menuItemId, variantId, modifierId)) : undefined) ??
    index.get(key(menuItemId, null, modifierId))
  );
}

export function resolveLine(index: RecipeIndex, line: SaleLineInput): ResolvedLine {
  const base = find(index, line.menuItemId, line.variantId, null);
  const totals = new Map<string, number>();

  const add = (recipe: ReplicaRecipe | undefined) => {
    for (const recipeLine of recipe?.lines ?? []) {
      totals.set(recipeLine.ingredientId, (totals.get(recipeLine.ingredientId) ?? 0) + recipeLine.quantity);
    }
  };

  add(base);
  for (const modifierId of line.modifierIds) {
    add(find(index, line.menuItemId, line.variantId, modifierId));
  }

  return {
    lineId: line.lineId,
    menuItemId: line.menuItemId,
    itemName: line.itemName,
    quantity: line.quantity,
    mapped: base !== undefined,
    // A substitute cancelling the dairy leaves milk at zero — nothing to move.
    perUnit: [...totals]
      .filter(([, quantity]) => quantity !== 0)
      .map(([ingredientId, quantity]) => ({ ingredientId, quantity })),
  };
}
